import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { DefaultModalProps, withLocationStateModal } from 'utils/modals';
import * as Yup from 'yup';

import { Box, Button, Grid, Typography } from '@mui/material';

import { useUpdateTalentEmailMutation } from '@libs/graphql-types';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';

import { ChangeEmailModalData, EditProfileModals } from './types';

interface ChangeEmailModalProps
  extends DefaultModalProps<ChangeEmailModalData> {
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  newEmail: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

interface FormValues {
  newEmail: string;
}

const ChangeEmailModalComponent: React.FC<ChangeEmailModalProps> = ({
  close,
  isOpen,
  modalData,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [updateTalentEmail, { loading }] = useUpdateTalentEmailMutation({
    onCompleted: () => {
      enqueueSnackbar(
        "We'll send a verification link to your new email. Your current email will remain active until verification is complete.",
        { variant: 'success' },
      );
      onSuccess?.();
      close();
    },
  });

  const initialValues: FormValues = {
    newEmail: modalData?.currentEmail || '',
  };

  const handleSubmit = (values: FormValues) => {
    updateTalentEmail({
      variables: {
        email: values.newEmail,
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, dirty, submitForm }) => {
        return (
          <DefaultModal
            open={isOpen}
            handleClose={close}
            title="Change Your Email Address"
            actions={
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    color="error"
                    variant="contained"
                    type="submit"
                    onClick={submitForm}
                    size="large"
                    disabled={!isValid || !dirty || loading}
                  >
                    {loading ? 'Sending...' : 'Send Verification'}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={close}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            }
          >
            <Box p={3}>
              <Typography variant="body2" color="textSecondary" mb={3}>
                We'll send a verification link to your new email. Your current
                email will remain active until verification is complete.
              </Typography>

              <Form>
                <Grid container spacing={3} sx={{ mb: 0 }}>
                  <Grid item xs={12}>
                    <ConnectedTextField
                      name="newEmail"
                      fullWidth
                      size="small"
                      variant="filled"
                      label="New Email Address"
                      type="email"
                      placeholder="Enter your new email address"
                    />
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </DefaultModal>
        );
      }}
    </Formik>
  );
};

export const ChangeEmailModal = withLocationStateModal<ChangeEmailModalData>({
  id: EditProfileModals.CHANGE_EMAIL,
})(ChangeEmailModalComponent);
